import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HasSubscriptions } from '../has-subscriptions';
import { DirectoryTypes, ListingCategory } from '../../classes/enums';
import { DIRECTORY_STRUCTURE_CONFIG} from '../../app-config.constants';
import { InternalLinksService } from '../../services/internalLinks.service';
import { CommunicationService } from '../../services/communication.service';
import { DirectoryPageResponse, DirectoryLinksPageData, CardLink, NeighbourhoodLinksResponse, NeighbourhoodLink, ProvinceLink } from '../../classes/interfaces';
import { MetaTagService } from '../../services/meta-tag.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-directory-page',
  templateUrl: './directory-page.component.html',
  styleUrls: ['./directory-page.component.scss']
})
export class DirectoryPageComponent extends HasSubscriptions implements OnInit {

  pageConfig;
  listingCategory: ListingCategory;
  directoryLinks: DirectoryLinksPageData[];
  metaPrefix: string;

  constructor(
    private activatedRoute: ActivatedRoute,
    private internalLinksService: InternalLinksService,
    private metaTagService: MetaTagService,
    private communicationService: CommunicationService
  ) {
    super();
  }

  ngOnInit() {
    this.addSub(this.activatedRoute.data.subscribe(({ directoryType }: { directoryType: DirectoryTypes }) => {
      this.setPageConfiguration(directoryType);
      this.setDirectories(directoryType);
      this.setSEOItems();
    }));
    this.addSub(
      this.communicationService.listings.currentShownListingType.subscribe((listingCategory) => {
        this.listingCategory = listingCategory;
      })
    );
    this.communicationService.footerLinksChange.next({city: null, province: null});
  }

  private setDirectories(directoryType: DirectoryTypes): void {
    let linksCall: Observable<any>;
    if (directoryType === DirectoryTypes.NEIGHBOURHOOD) {
      linksCall =  this.internalLinksService.getNeighborhoodLinksForProvinces(100).pipe(map((result: NeighbourhoodLinksResponse) => this.generateNeighbourhoodLinks(result)));
    } else {
      linksCall =  this.internalLinksService.getDirectoryForProvinces(this.listingCategory, directoryType).pipe(map((result: DirectoryPageResponse) => this.generateDirectoryCityLinks(result)));
    }

    this.addSub(
      linksCall.subscribe((data: DirectoryLinksPageData[]) => {
        this.directoryLinks = data;
      })
    );
  }

  private generateDirectoryCityLinks(result: DirectoryPageResponse): DirectoryLinksPageData[] {
    return result.provinceLinks.map((provinceLink: ProvinceLink) => {
      const stateShortLowerCase = provinceLink.stateShort.toLowerCase();
      return {
        province: `provinces.${stateShortLowerCase}`,
        visible: false,
        showMore: false,
        cityCardLinks: this.pageConfig.cardStructure ? this.generateCityCardLinks(provinceLink.cities, stateShortLowerCase) : null
      }
    });
  }

  private generateNeighbourhoodLinks(result: NeighbourhoodLinksResponse): DirectoryLinksPageData[] {
    return result.neighbourhoodLinks.map((neighbourhoodLink: NeighbourhoodLink) => {
      const stateShortLowerCase = neighbourhoodLink.stateShort.toLowerCase();
      const steps = ((neighbourhoodLink.neighbourhoods.length - 50 ) / 25);

      return {
        province: `provinces.${stateShortLowerCase}`,
        visible: false,
        showMore: 0,
        listLinks: neighbourhoodLink.neighbourhoods
        }
      }
    );
  }

  private generateCityCardLinks(cities: Array<string>, stateShortLowerCase: string): Array<CardLink> {
    return cities.map((city: string) => {
      const cityNoSpace = city.replace(/[^A-Z0-9]+/ig, '-').toLowerCase();
      return {
          title: city,
          main: {
            url: `/${stateShortLowerCase}/${cityNoSpace}${this.pageConfig.cardStructure.main.urlSuffix}`,
            title: this.pageConfig.cardStructure.main.title,
            icon: ''
          },
          subLink1: {
            url: `/${stateShortLowerCase}/${cityNoSpace}${this.pageConfig.cardStructure.sub1.urlSuffix}`,
            title: this.pageConfig.cardStructure.sub1.title,
            icon: this.pageConfig.cardStructure.sub1.icon
          },
          subLink2: {
            url: `/${stateShortLowerCase}/${cityNoSpace}${this.pageConfig.cardStructure.sub2.urlSuffix}`,
            title: this.pageConfig.cardStructure.sub2.title,
            icon:  this.pageConfig.cardStructure.sub2.icon
          }
      }
    })
  }

  private setSEOItems() {
    const title = `directory-page.meta-tags.${this.metaPrefix}.title`;
    const description = `directory-page.meta-tags.${this.metaPrefix}.description`;

    this.createMetaDataTags(title, description, {}, {});
  }

  private setPageConfiguration(directoryType: DirectoryTypes): void {
    switch (directoryType) {
      case DirectoryTypes.OPEN_HOUSE:
        this.metaPrefix = 'open-houses';
        this.pageConfig = DIRECTORY_STRUCTURE_CONFIG.openHouse;
        break;
      case DirectoryTypes.LISTING:
        this.metaPrefix = 'real-estate';
        this.pageConfig = DIRECTORY_STRUCTURE_CONFIG.listing;
        break;
      case DirectoryTypes.AGENT:
        this.metaPrefix = 'real-estate-agents';
        this.pageConfig = DIRECTORY_STRUCTURE_CONFIG.agent;
        break;
      default:
        this.metaPrefix = 'neighbourhood';
        this.pageConfig = DIRECTORY_STRUCTURE_CONFIG.neighborhood;
        break;
    }
  }

  createMetaDataTags(titleString: string, descriptionString: string, titleVariables: any, descriptionVariables: any) {
    this.metaTagService.setPageTitle(titleString, titleVariables);
    this.metaTagService.setPageDescription(descriptionString, descriptionVariables);

    const openGraphTags: any = [
      {
        name: 'title',
        content: titleString,
        variables: titleVariables,
        isTranslateKey: true
      },
      {
        name: 'description',
        content: descriptionString,
        variables: descriptionVariables,
        isTranslateKey: true
      }
    ];

    this.metaTagService.setOpenGraphTags(openGraphTags);
  }

  private updateShownValue(showMore: number): number {
    return ((showMore + 1) % 3);
  }
}

